import { Navigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Clock, 
  Shield, 
  Zap, 
  FileText, 
  CheckCircle2,
  ChevronRight
} from 'lucide-react';

export default function Landing() {
  const { login, user, loading } = useAuth();

  // If user is already signed in, send them straight to the dashboard
  // instead of forcing them to click "START FREE" again
  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const features = [
    {
      icon: <Zap className="w-5 h-5" />,
      title: "90 SECOND APPLICATIONS",
      description: "What takes 40 minutes by hand takes 90 seconds with us."
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "BANK-LEVEL SECURITY",
      description: "Your data is encrypted and never sold. Period."
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "AI DOCUMENT PARSING",
      description: "Upload once. Auto-fill everything, forever."
    },
    {
      icon: <Clock className="w-5 h-5" />,
      title: "TIME SAVED TRACKING",
      description: "See exactly how many hours you've reclaimed."
    }
  ];

  const useCases = [
    "JOB APPLICATIONS",
    "RENTAL FORMS",
    "LOAN APPLICATIONS",
    "GOVERNMENT SERVICES",
    "INSURANCE QUOTES",
    "BANK ACCOUNTS"
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Hero Section */}
      <section className="container mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 border border-border text-xs uppercase tracking-widest text-muted-foreground">
              <span className="w-2 h-2 bg-accent rounded-full animate-pulse"></span>
              BETA ACCESS OPEN
            </div>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight">
              STOP FILLING OUT
              <br />
              <span className="text-primary">THE SAME FORMS</span>
              <br />
              OVER AND OVER
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
              Build your life profile once. Auto-fill job applications, rental forms, 
              and government services in under 90 seconds. <span className="text-foreground font-medium">It's not magic. It's infrastructure.</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                onClick={login}
                className="brutalist-shadow uppercase tracking-wider group"
                data-testid="hero-cta"
              >
                START FREE
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="uppercase tracking-wider"
                onClick={() => window.location.href = '/extension'}
                data-testid="hero-extension"
              >
                GET THE EXTENSION
              </Button>
            </div>
            
            <div className="flex items-center gap-8 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                <span>INSTANT ACCESS</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-accent" />
                <span>CANCEL ANYTIME</span>
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-5">
            <div className="border border-border p-8 bg-card relative">
              <div className="absolute -top-3 left-4 px-2 bg-background text-xs uppercase tracking-widest text-muted-foreground">
                PROFILE STRENGTH
              </div>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <span className="font-heading text-3xl">87%</span>
                  <span className="text-accent text-sm uppercase">STRONG</span>
                </div>
                
                {['IDENTITY', 'WORK', 'FINANCIALS', 'DOCUMENTS'].map((section, i) => (
                  <div key={section} className="space-y-2">
                    <div className="flex justify-between text-xs uppercase tracking-wider">
                      <span>{section}</span>
                      <span className="text-muted-foreground">{[95, 88, 72, 92][i]}%</span>
                    </div>
                    <div className="h-1 bg-secondary">
                      <div 
                        className="h-full bg-primary transition-all duration-500"
                        style={{ width: `${[95, 88, 72, 92][i]}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Marquee */}
      <section className="border-y border-border py-4 overflow-hidden bg-secondary/30">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...useCases, ...useCases].map((useCase, i) => (
            <span key={i} className="mx-8 text-sm uppercase tracking-widest text-muted-foreground flex items-center gap-4">
              {useCase}
              <ChevronRight className="w-4 h-4" />
            </span>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl mb-4">HOW IT WORKS</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Upload your documents. We extract everything. You click autofill. That's it.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
          {features.map((feature, i) => (
            <div key={i} className="bg-background p-8 hover:bg-secondary/50 transition-colors" data-testid={`feature-${i}`}>
              <div className="w-10 h-10 border border-border flex items-center justify-center mb-4 text-primary">
                {feature.icon}
              </div>
              <h3 className="font-heading text-sm mb-2">{feature.title}</h3>
              <p className="text-sm text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Value Props Section */}
      <section className="border-y border-border">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-px bg-border">
            {[
              { value: "1X", label: "FILL YOUR PROFILE ONCE" },
              { value: "∞", label: "AUTOFILL UNLIMITED FORMS" },
              { value: "0", label: "RETYPING THE SAME INFO" }
            ].map((stat, i) => (
              <div key={i} className="bg-background p-12 text-center">
                <div className="font-heading text-5xl md:text-6xl mb-2">{stat.value}</div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 md:px-8 py-16 md:py-24">
        <div className="border border-border p-8 md:p-12 lg:p-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl mb-6">
            YOUR TIME IS WORTH MORE<br />THAN FILLING OUT FORMS
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Join thousands who've reclaimed their time. Start with 2 free autofills today.
          </p>
          <Button 
            size="lg" 
            onClick={login}
            className="brutalist-shadow uppercase tracking-wider"
            data-testid="cta-button"
          >
            CREATE YOUR PROFILE
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-6 h-6 bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-heading text-xs font-bold">LA</span>
              </div>
              <span className="text-sm text-muted-foreground">LIFE ADMIN AUTOFILL</span>
            </div>
            <div className="flex flex-wrap gap-6 text-xs uppercase tracking-wider text-muted-foreground">
              <a href="/help" className="hover:text-foreground transition-colors" data-testid="footer-help">Help</a>
              <a href="/contact" className="hover:text-foreground transition-colors" data-testid="footer-contact">Contact</a>
              <a href="/privacy" className="hover:text-foreground transition-colors" data-testid="footer-privacy">Privacy</a>
              <a href="/terms" className="hover:text-foreground transition-colors" data-testid="footer-terms">Terms</a>
              <a href="mailto:support@lifeautofill.com" className="hover:text-foreground transition-colors" data-testid="footer-email">support@lifeautofill.com</a>
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
      `}</style>
    </div>
  );
}
